import React, { lazy } from 'react'
import { Navigate, Route } from 'react-router-dom'
import { PRIVATE_ROUTES } from 'router/routes'
import RoutesWithNotFound from 'router/RoutesWithNotFound'
import RouteAccessGuard from 'guards/RouteAccessGuard'

// panel principal
const Dashboard = lazy(() => import('pages/dashboard/Dashboard'))
const UserServices = lazy(() => import('pages/user-services/UserServices'))
const OperationsMonitoring = lazy(() => import('pages/operations_monitoring/OperationsMonitoring'))

// reportes
const OperatingExpenses = lazy(() => import('pages/operating_expenses/OperatingExpenses'))
const AccountingReport = lazy(() => import('pages/accounting-report/AccountingReport'))
const ProductPrices = lazy(() => import('pages/product_prices/ProductPrices'))
const SalesBySeller = lazy(() => import('pages/sales_by_seller/SalesBySeller'))
const PurchasesReport = lazy(() => import('pages/purchases_report/PurchasesReport'))
const ValuedInventoryReport = lazy(() => import('pages/valued_inventory_report/ValuedInventoryReport'))
const ProfitReport = lazy(() => import('pages/profit_report/ProfitReport'))

// mantenedores
const Users = lazy(() => import('pages/users/Users'))
const TreasuryMovementTypes = lazy(() => import('pages/treasury-movement-types/TreasuryMovementTypes'))
const Vouchers = lazy(() => import('pages/vouchers/Vouchers'))
const People = lazy(() => import('pages/people/People'))
const Employees = lazy(() => import('pages/employees/Employees'))
const ManagementDriver = lazy(() => import('pages/management_driver/ManagementDriver'))
const VehicleStations = lazy(() => import('pages/vehicle_stations/VehicleStations'))
const Vehicles = lazy(() => import('pages/vehicles/Vehicles'))

// almacen
const Stores = lazy(() => import('pages/stores/Stores'))
const ProductRequests = lazy(() => import('pages/product_requests/ProductRequests'))
const Inventory = lazy(() => import('pages/inventory/Inventory'))
const Products = lazy(() => import('pages/products/Products'))
const Purchases = lazy(() => import('pages/purchases/Purchases'))
const PurchaseCorrectionPage = lazy(() => import('pages/purchases/correction/PurchaseCorrectionPage'))
const PurchaseCorrectionActionPage = lazy(() => import('pages/purchases/correction/PurchaseCorrectionActionPage'))
const Dispatches = lazy(() => import('pages/dispatches/Dispatches'))
const Entries = lazy(() => import('pages/entries/Entries'))
const Outputs = lazy(() => import('pages/outputs/Outputs'))
const ShippingGuides = lazy(() => import('pages/shipping-guides/ShippingGuides'))
const Transfers = lazy(() => import('pages/transfers/Transfers'))
const SeriesEntry = lazy(() => import('pages/series-entry/SeriesEntry'))
const Kardex = lazy(() => import('pages/kardex/Kardex'))
const ProductMobility = lazy(() => import('pages/products/ProductMobility'))

// cpe
const CPE = lazy(() => import('pages/cpe/CPE'))

// finanzas
const Treasury = lazy(() => import('pages/treasury/Treasury'))
const TreasuryMovements = lazy(() => import('pages/treasury-movments/TreasuryMovements'))
const ManageCash = lazy(() => import('pages/manage_cash/ManageCashV2'))
const CashRegister = lazy(() => import('pages/cash-register/CashRegister'))
const CashRegisterMovements = lazy(() => import('pages/cash-register-movements/CashRegisterMovements'))
const SaleRetainers = lazy(() => import('pages/sale-retainers/SaleRetainers'))
const CashReceipts = lazy(() => import('pages/cash_receipts/CashReceipts'))
const CashOutlays = lazy(() => import('pages/cash_outlays/CashOutlays'))
const CustomerCredits = lazy(() => import('pages/customer_credits/CustomerCredits'))
const ProviderCredits = lazy(() => import('pages/provider_credits/ProviderCredits'))

// cobranzas
const Loans = lazy(() => import('pages/loans/Loans'))
const LoansOpening = lazy(() => import('pages/loans_opening/LoansOpening'))
const LoanOpeningExpenses = lazy(() => import('pages/loan_opening_expenses/LoanOpeningExpenses'))
const LoanOpeningIncomes = lazy(() => import('pages/loan_opening_incomes/LoanOpeningIncomes'))
const LoansReport = lazy(() => import('pages/loans_report/LoansReport'))

// procesos
const Sales = lazy(() => import('pages/sales/Sales'))
const Orders = lazy(() => import('pages/orders/Orders'))
const Proformas = lazy(() => import('pages/proformas/Proformas'))
const CreditNotes = lazy(() => import('pages/credit_notes/CreditNotes'))
const Pos = lazy(() => import('pages/pos/Pos'))

const AttentionServiceOrders = lazy(() => import('pages/attention_service_orders/AttentionServiceOrders'))
const VehicleRequirements = lazy(() => import('pages/vehicle_requirements/VehicleRequirements'))
const VehicleOrderServices = lazy(() => import('pages/vehicle_service_orders/VehicleOrderServices'))

// seguridad
const AccessControl = lazy(() => import('pages/access_control/AccessControl'))
const MenuControl = lazy(() => import('pages/menu_control/MenuControl'))

// opciones
const Establishment = lazy(() => import('pages/establishment/Establishment'))
const Operators = lazy(() => import('pages/operators/Operators'))
const CommissionManagement = lazy(() => import('pages/commissions/CommissionManagement'))
const CommissionReport = lazy(() => import('pages/commission_report/CommissionReport'))

// reportes
const Reports = lazy(() => import('pages/reports/Reports'))
const Top10Clients = lazy(() => import('pages/reports/report-types/top-10-clients/Top10Clients'))
const ProductsByBrand = lazy(() => import('pages/reports/report-types/products-by-brand/ProductsByBrand'))
const PurchasesByBrand = lazy(() => import('pages/reports/report-types/purchases-by-brand/PurchasesByBrand'))
const SalesByVoucherType = lazy(() => import('pages/reports/report-types/sales-by-voucher-type/SalesByVoucherType'))

const Private = () => {
  return (
    <RoutesWithNotFound>
      <Route element={<RouteAccessGuard />}>
        <Route path="/" element={<Navigate to={PRIVATE_ROUTES.DASHBOARD} />} />
        <Route path={PRIVATE_ROUTES.DASHBOARD} element={<Dashboard />} />
        <Route path={PRIVATE_ROUTES.USER_SERVICES} element={<UserServices />} />
        <Route path={PRIVATE_ROUTES.OPERAIONS_MONITORING} element={<OperationsMonitoring />} />
        <Route path={PRIVATE_ROUTES.OPERATING_EXPENSES} element={<OperatingExpenses />} />
        <Route path={PRIVATE_ROUTES.ACCOUNTING_REPORT} element={<AccountingReport />} />
        <Route path={PRIVATE_ROUTES.PRODUCT_PRICES} element={<ProductPrices />} />
        <Route path={PRIVATE_ROUTES.SALES_BY_SELLER} element={<SalesBySeller />} />
        <Route path={PRIVATE_ROUTES.PURCHASES_REPORT} element={<PurchasesReport />} />
        <Route path={PRIVATE_ROUTES.VALUED_INVENTORY_REPORT} element={<ValuedInventoryReport />} />
        <Route path={PRIVATE_ROUTES.PROFIT_REPORT} element={<ProfitReport />} />
        <Route path={PRIVATE_ROUTES.USERS} element={<Users />} />
        <Route path={PRIVATE_ROUTES.TREASURY_MOVEMENT_TYPES} element={<TreasuryMovementTypes />} />
        <Route path={PRIVATE_ROUTES.VOUCHERS} element={<Vouchers />} />
        <Route path={PRIVATE_ROUTES.PEOPLE} element={<People />} />
        <Route path={PRIVATE_ROUTES.EMPLOYEES} element={<Employees />} />
        <Route path={PRIVATE_ROUTES.MANAGEMENT_DRIVER} element={<ManagementDriver />} />
        <Route path={PRIVATE_ROUTES.STATIONS} element={<VehicleStations />} />
        <Route path={PRIVATE_ROUTES.VEHICLES} element={<Vehicles />} />
        <Route path={PRIVATE_ROUTES.STORES} element={<Stores />} />
        <Route path={PRIVATE_ROUTES.PRODUCT_REQUESTS} element={<ProductRequests />} />
        <Route path={PRIVATE_ROUTES.INVENTORY} element={<Inventory />} />
        <Route path={PRIVATE_ROUTES.PRODUCTS} element={<Products />} />
        <Route path={PRIVATE_ROUTES.PURCHASES} element={<Purchases />} />
        <Route path={PRIVATE_ROUTES.PURCHASE_CORRECTION} element={<PurchaseCorrectionPage />} />
        <Route path={PRIVATE_ROUTES.PURCHASE_CORRECTION_ACTION} element={<PurchaseCorrectionActionPage />} />
        <Route path={PRIVATE_ROUTES.DISPATCHES} element={<Dispatches />} />
        <Route path={PRIVATE_ROUTES.ENTRIES} element={<Entries />} />
        <Route path={PRIVATE_ROUTES.OUTPUTS} element={<Outputs />} />
        <Route path={PRIVATE_ROUTES.SHIPPING_GUIDES} element={<ShippingGuides />} />
        <Route path={PRIVATE_ROUTES.TRANSFERS} element={<Transfers />} />
        <Route path={PRIVATE_ROUTES.SERIES_ENTRY} element={<SeriesEntry />} />
        <Route path={PRIVATE_ROUTES.KARDEX} element={<Kardex />} />
        <Route path={PRIVATE_ROUTES.PRODUCT_MOBILITY} element={<ProductMobility />} />
        <Route path={PRIVATE_ROUTES.CPE} element={<CPE />} />
        <Route path={PRIVATE_ROUTES.TREASURY} element={<Treasury />} />
        <Route path={PRIVATE_ROUTES.TREASURY_MOVEMENTS} element={<TreasuryMovements />} />
        <Route path={PRIVATE_ROUTES.MANAGE_CASH} element={<ManageCash />} />
        <Route path={PRIVATE_ROUTES.CASH_REGISTER} element={<CashRegister />} />
        <Route path={PRIVATE_ROUTES.CASH_REGISTER_MOVEMENTS} element={<CashRegisterMovements />} />
        <Route path={PRIVATE_ROUTES.SALE_RETAINERS} element={<SaleRetainers />} />
        <Route path={PRIVATE_ROUTES.CASH_RECEIPTS} element={<CashReceipts />} />
        <Route path={PRIVATE_ROUTES.CASH_OUTLAYS} element={<CashOutlays />} />
        <Route path={PRIVATE_ROUTES.CUSTOMER_CREDITS} element={<CustomerCredits />} />
        <Route path={PRIVATE_ROUTES.PROVIDER_CREDITS} element={<ProviderCredits />} />

        <Route path={PRIVATE_ROUTES.LOANS} element={<Loans />} />
        <Route path={PRIVATE_ROUTES.LOANS_OPENING} element={<LoansOpening />} />
        <Route path={PRIVATE_ROUTES.LOAN_OPENING_EXPENSES} element={<LoanOpeningExpenses />} />
        <Route path={PRIVATE_ROUTES.LOAN_OPENING_INCOMES} element={<LoanOpeningIncomes />} />
        <Route path={PRIVATE_ROUTES.LOANS_REPORT} element={<LoansReport />} />

        <Route path={PRIVATE_ROUTES.ATTENTION_SERVICE_ORDERS} element={<AttentionServiceOrders />} />
        <Route path={PRIVATE_ROUTES.VEHICLE_REQUIREMENTS} element={<VehicleRequirements />} />
        <Route path={PRIVATE_ROUTES.VEHICLE_ORDER_SERVICES} element={<VehicleOrderServices />} />

        <Route path={PRIVATE_ROUTES.SALES} element={<Sales />} />
        <Route path={PRIVATE_ROUTES.ORDERS} element={<Orders />} />
        <Route path={PRIVATE_ROUTES.PROFORMAS} element={<Proformas />} />
        <Route path={PRIVATE_ROUTES.CREDIT_NOTES} element={<CreditNotes />} />
        <Route path={PRIVATE_ROUTES.ACCESS_CONTROL} element={<AccessControl />} />
        <Route path={PRIVATE_ROUTES.MENU_CONTROL} element={<MenuControl />} />
        <Route path={PRIVATE_ROUTES.ESTABLISHMENT} element={<Establishment />} />

        <Route path={PRIVATE_ROUTES.REPORTS} element={<Reports />} />
        <Route path={PRIVATE_ROUTES.REPORTS_TOP_10_CLIENTS} element={<Top10Clients />} />
        <Route path={PRIVATE_ROUTES.REPORTS_PRODUCTS_BY_BRAND} element={<ProductsByBrand />} />
        <Route path={PRIVATE_ROUTES.REPORTS_PURCHASES_BY_BRAND} element={<PurchasesByBrand />} />
        <Route path={PRIVATE_ROUTES.REPORTS_SALES_BY_VOUCHER_TYPE} element={<SalesByVoucherType />} />
        <Route path={PRIVATE_ROUTES.POS} element={<Pos />} />
        <Route path={PRIVATE_ROUTES.OPERATORS} element={<Operators />} />
        <Route path={PRIVATE_ROUTES.COMMISSION_MANAGEMENT} element={<CommissionManagement />} />
        <Route path={PRIVATE_ROUTES.COMMISSION_REPORT} element={<CommissionReport />} />
      </Route>
    </RoutesWithNotFound>
  )
}

export default Private